import React, { useState } from 'react';
import AdminList, { fmtINR, fmtDate, StatusChip, statusToneOrder, statusTonePayment } from './AdminList';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/* -------------------------------------------------------------
 * Compact single-file bundle for every remaining Admin page.
 * Each page = ~20-40 lines of config → AdminList.
 * Detail modals for Orders / Buyers / Conversations included.
 * ------------------------------------------------------------- */

// ============================================================ BUYERS
export function AdminBuyers() {
  const [pick, setPick] = useState(null);
  return (
    <>
      <AdminList
        pageTitle="Buyers" endpoint="/admin/buyers" csvEntity="buyers"
        testidBase="admin-buyers"
        onRowClick={(row) => setPick(row.id)}
        columns={[
          { key: 'name',  label: 'Name',      render: (r) => r.name || r.phone || '—' },
          { key: 'phone', label: 'Phone' },
          { key: 'seller_id', label: 'Seller',       width: 260, render: (r) => <code className="text-[10px]">{r.seller_id?.slice(0, 8)}…</code> },
          { key: 'total_orders', label: 'Orders' },
          { key: 'total_spent',  label: 'Spent',     render: (r) => fmtINR(r.total_spent) },
          { key: 'last_active_at', label: 'Last active', render: (r) => fmtDate(r.last_active_at) },
        ]}
      />
      {pick && <BuyerDetail id={pick} onClose={() => setPick(null)} />}
    </>
  );
}
function BuyerDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  React.useEffect(() => { api.get(`/admin/buyers/${id}`).then(({ data }) => setD(data)); }, [id]);
  return (
    <Modal onClose={onClose} title="Buyer Detail" testid="admin-buyer-detail">
      {!d ? 'Loading…' : (<>
        <div className="text-[12.5px]"><strong>{d.buyer.name}</strong> · {d.buyer.phone}</div>
        <div className="text-[11px] text-fab-text-muted">Total spent: {fmtINR(d.buyer.total_spent)} · Orders: {d.buyer.total_orders}</div>
        <div className="mt-3 text-[11px] uppercase font-semibold text-fab-text-muted">Conversations across sellers</div>
        <ul className="text-[12px]">{(d.conversations || []).map((c) => <li key={c.id}>· seller {c.seller_id?.slice(0,8)}… · updated {fmtDate(c.updated_at)}</li>)}</ul>
        <div className="mt-3 text-[11px] uppercase font-semibold text-fab-text-muted">Recent orders</div>
        <ul className="text-[12px]">{(d.orders || []).map((o) => <li key={o.id}>· {o.order_number} · {fmtINR(o.total_amount)} · {o.status}</li>)}</ul>
      </>)}
    </Modal>
  );
}

// ============================================================ CONVERSATIONS
export function AdminConversations() {
  const [pick, setPick] = useState(null);
  return (<>
    <AdminList
      pageTitle="Conversations" endpoint="/admin/conversations"
      testidBase="admin-conversations"
      onRowClick={(row) => setPick(`${row.seller_id}:${row.buyer_id}`)}
      columns={[
        { key: 'seller_name', label: 'Seller' },
        { key: 'buyer_id',    label: 'Buyer',    render: (r) => <code className="text-[10.5px]">{r.buyer_id?.slice(0, 12)}…</code> },
        { key: 'last_collection_id', label: 'Current collection', render: (r) => <code className="text-[10.5px]">{r.last_collection_id?.slice(0,8) || '—'}</code> },
        { key: 'last_message_preview', label: 'Last message preview' },
        { key: 'updated_at', label: 'Updated', render: (r) => fmtDate(r.updated_at) },
      ]}
    />
    {pick && <ConversationDetail cid={pick} onClose={() => setPick(null)} />}
  </>);
}
function ConversationDetail({ cid, onClose }) {
  const [d, setD] = useState(null);
  React.useEffect(() => { api.get(`/admin/conversations/${cid}`).then(({ data }) => setD(data)); }, [cid]);
  return (
    <Modal wide onClose={onClose} title="Conversation" testid="admin-conversation-detail">
      {!d ? 'Loading…' : (
        <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-1">
          <div className="text-[11px] text-fab-text-muted">seller {d.seller_id?.slice(0,8)}… · buyer {d.buyer_id?.slice(0,12)}… · last collection {d.last_collection_id?.slice(0,8) || '—'}</div>
          {(d.messages || []).map((m, i) => (
            <div key={i} className={`text-[12px] px-2 py-1 rounded ${m.role === 'buyer' ? 'bg-fab-wa/10' : 'bg-white border border-fab-primary-lighter'}`}>
              <span className="font-semibold text-[10.5px] uppercase tracking-wider text-fab-text-muted">{m.role}</span>
              {m.kind && <span className="ml-1 text-[10.5px] text-fab-primary">· {m.kind}</span>}
              <span className="ml-1 text-[10.5px] text-fab-text-muted">· {fmtDate(m.at)}</span>
              <div className="mt-0.5 whitespace-pre-line text-[12.5px] text-fab-text">{m.text || (m.url ? `[${m.kind || 'media'}] ${m.url}` : '')}</div>
              {m.screenshot_url && <div className="mt-1 text-[10.5px] text-fab-primary">screenshot: {m.screenshot_url.slice(-30)}</div>}
              {m.parcel_image_url && <div className="mt-1 text-[10.5px] text-fab-primary">parcel: {m.parcel_image_url.slice(-30)}</div>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ============================================================ PRODUCTS / COLLECTIONS
export function AdminProducts() {
  return <AdminList pageTitle="Products" endpoint="/admin/products" csvEntity="products" testidBase="admin-products"
    filters={[{ key: 'in_stock', label: 'Stock',
                options: [{ value: 'true', label: 'In stock' }, { value: 'false', label: 'Out of stock' }] }]}
    columns={[
      { key: 'product_code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'price', label: 'Price', render: (r) => fmtINR(r.price) },
      { key: 'stock_quantity', label: 'Stock' },
      { key: 'seller_id', label: 'Seller', render: (r) => <code className="text-[10.5px]">{r.seller_id?.slice(0,8)}…</code> },
      { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
    ]} />;
}
export function AdminCollections() {
  return <AdminList pageTitle="Collections" endpoint="/admin/collections" csvEntity="collections" testidBase="admin-collections"
    columns={[
      { key: 'short_code', label: 'Code' }, { key: 'name', label: 'Name' },
      { key: 'seller_id', label: 'Seller', render: (r) => <code className="text-[10.5px]">{r.seller_id?.slice(0,8)}…</code> },
      { key: 'product_ids', label: 'Products', render: (r) => (r.product_ids || []).length },
      { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
      { key: 'id', label: 'Link', render: (r) => <a target="_blank" rel="noreferrer" href={`/c/${r.id}`} className="text-fab-primary underline">open</a> },
    ]} />;
}

// ============================================================ ORDERS / PAYMENTS / SHIPPING
export function AdminOrders() {
  const [pick, setPick] = useState(null);
  return (<>
    <AdminList pageTitle="Orders" endpoint="/admin/orders" csvEntity="orders" testidBase="admin-orders"
      filters={[{ key: 'status', label: 'Status',
        options: ['pending_payment','awaiting','confirmed','dispatched','cancelled'].map((v) => ({ value: v, label: v })) }]}
      onRowClick={(r) => setPick(r.id)}
      columns={[
        { key: 'order_number', label: 'Order' },
        { key: 'buyer_name',   label: 'Buyer', render: (r) => `${r.buyer_name || '—'} · ${r.buyer_phone || ''}` },
        { key: 'total_amount', label: 'Total', render: (r) => fmtINR(r.total_amount) },
        { key: 'status',       label: 'Status', render: (r) => <StatusChip value={r.status} tone={statusToneOrder(r.status)} /> },
        { key: 'payment.status', label: 'Payment', render: (r) => <StatusChip value={r.payment?.status || '—'} tone={statusTonePayment(r.payment?.status)} /> },
        { key: 'created_at',   label: 'Created', render: (r) => fmtDate(r.created_at) },
      ]} />
    {pick && <OrderDetailModal id={pick} onClose={() => setPick(null)} />}
  </>);
}
function OrderDetailModal({ id, onClose }) {
  const [o, setO] = useState(null);
  React.useEffect(() => { api.get(`/admin/orders/${id}`).then(({ data }) => setO(data)); }, [id]);
  return (
    <Modal wide onClose={onClose} title="Order Detail" testid="admin-order-detail">
      {!o ? 'Loading…' : (
        <div className="text-[12px] space-y-2">
          <div><strong>{o.order_number}</strong> · {fmtINR(o.total_amount)} · <StatusChip value={o.status} tone={statusToneOrder(o.status)} /></div>
          <div>Seller: <code>{o.seller_id?.slice(0,8)}…</code></div>
          <div>Buyer: {o.buyer_name} · {o.buyer_phone}</div>
          <div>Items:</div>
          <ul className="pl-4">{(o.items || []).map((it, i) => <li key={i}>· {it.quantity} × {it.product_code} @ {fmtINR(it.unit_price)}</li>)}</ul>
          <div>Payment: <StatusChip value={o.payment?.status || '—'} tone={statusTonePayment(o.payment?.status)} /></div>
          {o.payment?.screenshot_url && (
            <a target="_blank" rel="noreferrer" className="text-fab-primary underline text-[11px]"
               href={o.payment.screenshot_url.startsWith('http') ? o.payment.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}${o.payment.screenshot_url}`}>
              view payment screenshot
            </a>
          )}
          <div>Shipment: {o.shipment?.status || '—'}</div>
          {o.shipment?.parcel_image_url && (
            <a target="_blank" rel="noreferrer" className="text-fab-primary underline text-[11px]"
               href={o.shipment.parcel_image_url.startsWith('http') ? o.shipment.parcel_image_url : `${process.env.REACT_APP_BACKEND_URL}${o.shipment.parcel_image_url}`}>
              view parcel image · dispatched {fmtDate(o.shipment.dispatched_at)}
            </a>
          )}
          <div className="mt-2 text-[11px] uppercase font-semibold text-fab-text-muted">Timeline</div>
          <ul className="text-[11px] pl-3">{(o.timeline || []).map((t, i) => <li key={i}>· {fmtDate(t.at)} — {t.event}</li>)}</ul>
        </div>
      )}
    </Modal>
  );
}

export function AdminPayments() {
  return <AdminList pageTitle="Payments" endpoint="/admin/payments" csvEntity="payments" testidBase="admin-payments"
    filters={[{ key: 'status', label: 'Payment status',
      options: ['PENDING','SCREENSHOT_UPLOADED','VERIFIED','REJECTED','UNDERPAID'].map((v) => ({ value: v, label: v })) }]}
    columns={[
      { key: 'order_number', label: 'Order' },
      { key: 'buyer_phone', label: 'Buyer' },
      { key: 'total_amount', label: 'Amount', render: (r) => fmtINR(r.total_amount) },
      { key: 'payment.status', label: 'Payment', render: (r) => <StatusChip value={r.payment?.status || '—'} tone={statusTonePayment(r.payment?.status)} /> },
      { key: 'payment.screenshot_url', label: 'Proof',
        render: (r) => r.payment?.screenshot_url
          ? <a target="_blank" rel="noreferrer" className="text-fab-primary underline"
               href={r.payment.screenshot_url.startsWith('http') ? r.payment.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}${r.payment.screenshot_url}`}>view</a>
          : '—' },
      { key: 'created_at', label: 'Submitted', render: (r) => fmtDate(r.payment?.submitted_at || r.created_at) },
    ]} />;
}

export function AdminShipping() {
  return <AdminList pageTitle="Shipping" endpoint="/admin/shipping" testidBase="admin-shipping"
    filters={[{ key: 'status', label: 'Status',
      options: [{ value: 'pending', label: 'Awaiting dispatch' }, { value: 'dispatched', label: 'Dispatched' }] }]}
    columns={[
      { key: 'order_number', label: 'Order' },
      { key: 'buyer_name',   label: 'Buyer' },
      { key: 'status',       label: 'Status', render: (r) => <StatusChip value={r.status} tone={statusToneOrder(r.status)} /> },
      { key: 'shipment.parcel_image_url', label: 'Parcel image',
        render: (r) => r.shipment?.parcel_image_url
          ? <a target="_blank" rel="noreferrer" className="text-fab-primary underline"
               href={r.shipment.parcel_image_url.startsWith('http') ? r.shipment.parcel_image_url : `${process.env.REACT_APP_BACKEND_URL}${r.shipment.parcel_image_url}`}>view</a>
          : '—' },
      { key: 'shipment.dispatched_at', label: 'Dispatched at', render: (r) => fmtDate(r.shipment?.dispatched_at) },
    ]} />;
}

// ============================================================ REVIEWS
export function AdminReviews() {
  const [platform, setPlatform] = React.useState(null);
  React.useEffect(() => {
    api.get('/admin/platform-rating').then(({ data }) => setPlatform(data)).catch(() => setPlatform({ average: null, count: 0, distribution: {}, sellers_with_reviews: 0 }));
  }, []);
  return (
    <div className="space-y-4" data-testid="admin-reviews">
      {platform && (
        <div className="bg-white border border-fab-primary-lighter rounded-xl p-4" data-testid="admin-platform-rating">
          <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">FABTEXS AI Platform Rating</div>
          {platform.average != null && platform.count > 0 ? (
            <>
              <div className="mt-1 font-display text-3xl font-bold text-fab-text" data-testid="admin-platform-rating-value">
                <span className="text-amber-500">★</span> {platform.average} / 5
              </div>
              <div className="text-[12.5px] text-fab-text-secondary" data-testid="admin-platform-rating-count">
                {platform.count} verified buyer rating{platform.count === 1 ? '' : 's'} · {platform.sellers_with_reviews} seller{platform.sellers_with_reviews === 1 ? '' : 's'}
              </div>
              <div className="mt-3 space-y-1 max-w-md" data-testid="admin-platform-distribution">
                {[5, 4, 3, 2, 1].map((star) => {
                  const c = (platform.distribution || {})[star] || (platform.distribution || {})[String(star)] || 0;
                  const pct = platform.count ? Math.round((c / platform.count) * 100) : 0;
                  return (
                    <div key={star} className="flex items-center gap-2 text-[12px]">
                      <span className="w-8">{star}★</span>
                      <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-10 text-right text-fab-text-secondary">{c}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 text-[10.5px] text-fab-text-muted">
                Calculation: {platform.calc_method}. Test/admin reviews excluded: {platform.excluded_test_reviews}.
                {platform.average_of_averages_would_be != null && (<> · If we (wrongly) averaged seller-averages we would get {platform.average_of_averages_would_be} — kept for admin visibility only.</>)}
              </div>
            </>
          ) : (
            <div className="mt-2 text-[13px] text-fab-text-muted">FABTEXS AI — New · No platform rating yet</div>
          )}
        </div>
      )}
      <AdminList pageTitle="All verified reviews" endpoint="/admin/reviews" csvEntity="reviews" testidBase="admin-reviews-list"
        searchable={false}
        columns={[
          { key: 'seller_id', label: 'Seller',  render: (r) => <code className="text-[10.5px]">{r.seller_id?.slice(0,8)}…</code> },
          { key: 'order_id',  label: 'Order',   render: (r) => <code className="text-[10.5px]">{r.order_number || r.order_id?.slice(0,8) + '…'}</code> },
          { key: 'rating',    label: 'Stars',   render: (r) => '★'.repeat(Math.round(r.rating || 0)) },
          { key: 'review',    label: 'Text' },
          { key: 'is_test',   label: 'Test?',   render: (r) => r.is_test ? <StatusChip value="EXCLUDED" tone="neutral" /> : <StatusChip value="COUNTED" tone="green" /> },
          { key: 'created_at', label: 'Date',   render: (r) => fmtDate(r.created_at) },
        ]} />
    </div>
  );
}

// ============================================================ SUBSCRIPTIONS
export function AdminSubscriptions() {
  return <AdminList pageTitle="Subscriptions & Billing" endpoint="/admin/subscriptions" testidBase="admin-subs"
    searchable={false}
    filters={[{ key: 'status', label: 'Status',
      options: ['ACTIVE','TRIAL','PAST_DUE','CANCELLED','SUSPENDED'].map((v) => ({ value: v, label: v })) }]}
    columns={[
      { key: 'business_name', label: 'Seller' },
      { key: 'email',        label: 'Email' },
      { key: 'plan_tier',    label: 'Plan' },
      { key: 'plan_status',  label: 'Status', render: (r) => <StatusChip value={r.plan_status || '—'} tone={{ ACTIVE:'green', TRIAL:'blue', PAST_DUE:'orange', CANCELLED:'red', SUSPENDED:'red' }[r.plan_status] || 'neutral'} /> },
      { key: 'subscription_status', label: 'Sub state', render: (r) => r.subscription_status ? <StatusChip value={r.subscription_status.toUpperCase()} tone={{ active:'green', authenticated:'blue', pending:'orange', halted:'red', cancelled:'red', completed:'neutral', created:'neutral' }[r.subscription_status] || 'neutral'} /> : '—' },
      { key: 'billing_cycle', label: 'Cycle', render: (r) => r.billing_cycle || '—' },
      { key: 'current_period_end', label: 'Period ends', render: (r) => fmtDate(r.current_period_end) },
      { key: 'next_billing_at', label: 'Next billing', render: (r) => fmtDate(r.next_billing_at) },
      { key: 'cancel_at_cycle_end', label: 'Cancel@end', render: (r) => r.cancel_at_cycle_end ? '✓' : '—' },
      { key: 'trial_ends_at', label: 'Trial ends', render: (r) => fmtDate(r.trial_ends_at) },
      { key: 'admin_lifetime_free', label: 'Free lifetime', render: (r) => r.admin_lifetime_free ? '✓' : '—' },
      { key: 'razorpay_subscription_id', label: 'Razorpay Sub', render: (r) => r.razorpay_subscription_id ? <code className="text-[10.5px]">{r.razorpay_subscription_id}</code> : '—' },
    ]} />;
}

// ============================================================ COUPONS (CRUD)
export function AdminCoupons() {
  const [creating, setCreating] = useState(false);
  const [items, setItems] = useState(null);
  const load = () => api.get('/admin/coupons').then(({ data }) => setItems(data.items || []));
  React.useEffect(() => { load(); }, []);
  const create = async (code, benefit, max_uses) => {
    try { await api.post('/admin/coupons', { code, benefit, max_uses });
      toast.success('Coupon created'); setCreating(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggle = async (id) => {
    try { await api.post(`/admin/coupons/${id}/toggle`); load(); }
    catch { toast.error('Toggle failed'); }
  };
  return (
    <div className="space-y-3" data-testid="admin-coupons">
      <div className="flex items-center gap-2">
        <h2 className="font-display text-lg font-bold text-fab-text">Coupons</h2>
        <button onClick={() => setCreating(true)} className="ml-auto btn-primary text-[12px] px-3 py-1.5 rounded-md" data-testid="admin-coupon-new">+ New coupon</button>
      </div>
      <div className="bg-white border border-fab-primary-lighter rounded-lg overflow-hidden">
        <table className="min-w-full text-[12.5px]">
          <thead className="bg-fab-primary/5"><tr>{['Code','Benefit','Used / Max','Active','Expires','Actions'].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px] tracking-wider">{h}</th>)}</tr></thead>
          <tbody>
            {!items ? <tr><td colSpan={6} className="px-3 py-6 text-center text-fab-text-muted">Loading…</td></tr>
             : items.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-fab-text-muted">No coupons yet</td></tr>
             : items.map((c, i) => (
              <tr key={c.id} className="border-t border-fab-primary-lighter/60" data-testid={`admin-coupon-row-${i}`}>
                <td className="px-3 py-2 font-mono">{c.code}</td>
                <td className="px-3 py-2">{c.benefit}</td>
                <td className="px-3 py-2">{c.used_count} / {c.max_uses}</td>
                <td className="px-3 py-2">{c.active ? <StatusChip value="ACTIVE" tone="green" /> : <StatusChip value="OFF" tone="neutral" />}</td>
                <td className="px-3 py-2">{fmtDate(c.expires_at)}</td>
                <td className="px-3 py-2"><button onClick={() => toggle(c.id)} className="text-fab-primary text-[11px] underline" data-testid={`admin-coupon-toggle-${i}`}>{c.active ? 'Deactivate' : 'Activate'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && <NewCouponModal onClose={() => setCreating(false)} onCreate={create} />}
    </div>
  );
}
function NewCouponModal({ onClose, onCreate }) {
  const [code, setCode] = useState('');
  const [benefit, setBenefit] = useState('1_YEAR_FREE');
  const [maxUses, setMaxUses] = useState(1);
  return (
    <Modal onClose={onClose} title="New coupon" testid="admin-coupon-new-modal">
      <div className="space-y-3">
        <label className="block"><span className="text-[11px] uppercase text-fab-text-muted">Code</span>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="w-full border rounded px-2 py-1 mt-1" data-testid="admin-coupon-code" /></label>
        <label className="block"><span className="text-[11px] uppercase text-fab-text-muted">Benefit</span>
          <select value={benefit} onChange={(e) => setBenefit(e.target.value)} className="w-full border rounded px-2 py-1 mt-1" data-testid="admin-coupon-benefit">
            <option value="1_YEAR_FREE">1 account · 1 YEAR FREE</option>
            <option value="6_MONTHS_FREE">1 account · 6 months free</option>
            <option value="PERCENT_OFF:20">20% off first month</option>
          </select></label>
        <label className="block"><span className="text-[11px] uppercase text-fab-text-muted">Max uses</span>
          <input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(+e.target.value)} className="w-full border rounded px-2 py-1 mt-1" data-testid="admin-coupon-max" /></label>
        <button onClick={() => onCreate(code, benefit, maxUses)} className="btn-primary w-full py-2 rounded-md text-[13px]" data-testid="admin-coupon-create">Create</button>
      </div>
    </Modal>
  );
}

// ============================================================ NOTIFICATIONS / WHATSAPP / AI / HEALTH / SETTINGS
export function AdminNotifications() {
  return <AdminList pageTitle="Notifications" endpoint="/admin/notifications" testidBase="admin-notifs" searchable={false}
    filters={[{ key: 'type', label: 'Type', options: ['ORDER_CONFIRMED','PAYMENT_VERIFICATION_REQUIRED','PAYMENT_UNDERPAID','BROADCAST_SENT'].map((v) => ({ value: v, label: v })) }]}
    columns={[
      { key: 'seller_id', label: 'Seller', render: (r) => <code className="text-[10.5px]">{r.seller_id?.slice(0,8)}…</code> },
      { key: 'type',      label: 'Type' },
      { key: 'title',     label: 'Title' },
      { key: 'body',      label: 'Body' },
      { key: 'created_at', label: 'When',   render: (r) => fmtDate(r.created_at) },
    ]} />;
}
export function AdminWhatsApp() {
  return <AdminList pageTitle="WhatsApp connections" endpoint="/admin/whatsapp/connections" testidBase="admin-wa" searchable={false}
    columns={[
      { key: 'seller_id', label: 'Seller', render: (r) => <code className="text-[10.5px]">{r.seller_id?.slice(0,8)}…</code> },
      { key: 'display_phone_number', label: 'Phone' },
      { key: 'phone_number_id', label: 'Phone Number ID (masked)' },
      { key: 'status',    label: 'Status', render: (r) => <StatusChip value={r.status || '—'} tone={r.status === 'CONNECTED' ? 'green' : 'neutral'} /> },
      { key: 'created_at', label: 'Since', render: (r) => fmtDate(r.created_at) },
    ]} />;
}
export function AdminAIActivity() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { api.get('/admin/ai-activity').then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-fab-text-muted"><Loader2 className="inline w-4 h-4 animate-spin" /> loading…</div>;
  return (
    <div className="space-y-3" data-testid="admin-ai-activity">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(d.totals).map(([k, v]) => (
          <div key={k} className="bg-white border border-fab-primary-lighter rounded-xl p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-fab-text-muted font-semibold">{k.replaceAll('_',' ')}</div>
            <div className="font-display text-2xl font-bold text-fab-text mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-fab-primary-lighter rounded-lg overflow-hidden">
        <table className="min-w-full text-[12px]">
          <thead className="bg-fab-primary/5"><tr>{['Event','Seller','Buyer','Order','When'].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold text-fab-text-secondary uppercase text-[10.5px]">{h}</th>)}</tr></thead>
          <tbody>{(d.events || []).map((e, i) => (
            <tr key={i} className="border-t border-fab-primary-lighter/60">
              <td className="px-3 py-1.5">{e.kind}</td>
              <td className="px-3 py-1.5"><code className="text-[10.5px]">{e.seller_id?.slice(0,8)}…</code></td>
              <td className="px-3 py-1.5"><code className="text-[10.5px]">{e.buyer_id?.slice(0,10)}…</code></td>
              <td className="px-3 py-1.5">{e.order_number || '—'}</td>
              <td className="px-3 py-1.5">{fmtDate(e.at)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
export function AdminSystemHealth() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { api.get('/admin/system-health').then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-fab-text-muted"><Loader2 className="inline w-4 h-4 animate-spin" /> loading…</div>;
  const tone = { GREEN: 'green', WARNING: 'orange', RED: 'red', UNAVAILABLE: 'neutral' };
  return (
    <div className="space-y-3" data-testid="admin-system-health">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(d.checks || {}).map(([name, r]) => (
          <div key={name} className="bg-white border border-fab-primary-lighter rounded-xl p-3" data-testid={`admin-health-${name}`}>
            <div className="flex items-center gap-2">
              <StatusChip value={r.status} tone={tone[r.status] || 'neutral'} />
              <div className="font-semibold text-[13px] text-fab-text">{name}</div>
            </div>
            <div className="text-[11.5px] text-fab-text-muted mt-1">{r.detail}</div>
          </div>
        ))}
      </div>
      <div className="text-[10.5px] text-fab-text-muted">Generated at {fmtDate(d.generated_at)}</div>
    </div>
  );
}
export function AdminSettings() {
  const [me, setMe] = React.useState(null);
  React.useEffect(() => { api.get('/admin/me').then(({ data }) => setMe(data)); }, []);
  if (!me) return null;
  return (
    <div className="max-w-xl bg-white border border-fab-primary-lighter rounded-xl p-5" data-testid="admin-settings">
      <div className="text-[12px] uppercase text-fab-text-muted">Signed in as</div>
      <div className="font-display text-lg text-fab-text">{me.email}</div>
      <div className="mt-2 text-[12px]">Role: <strong>PLATFORM_ADMIN</strong></div>
      <div className="text-[12px]">Billing: {me.admin_lifetime_free ? <StatusChip value="LIFETIME FREE" tone="green" /> : '—'}</div>
      <div className="mt-3 text-[11.5px] text-fab-text-muted">Admin actions are logged in the Audit Log. Destructive actions require confirmation.</div>
    </div>
  );
}

// ============================================================ shared modal
function Modal({ title, testid, wide = false, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
        className={`bg-white rounded-xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} p-5`}>
        <div className="flex items-start justify-between mb-3">
          <div className="font-display text-lg font-bold text-fab-text">{title}</div>
          <button onClick={onClose} className="text-fab-text-muted text-[13px]" data-testid={`${testid}-close`}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}
